import React, { useState, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { Upload } from 'lucide-react';

import useAssetApi from './config';
import { useAuth } from '../../context/AuthContext';
import { parseTableParams } from '../../utils/Utils';
import DataTable from '../../components/ui/DataTable';
import Button from '../../components/ui/Button';
import GenericFormDrawer from '../../components/GenericFormDrawer';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import OptionsMenu from '../../components/ui/OptionsMenu';

const Container = styled.div``;

export default function AssetsPage() {
  const { t } = useTranslation();
  const { canWrite } = useAuth();
  const history = useHistory();

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [queryParams, setQueryParams] = useState({ pageSize: 10, pageNumber: 1 });
  const [filterList, setFilterList] = useState([]);
  const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, ids: [], name: '' });
  const [deleting, setDeleting] = useState(false);

  const { objectSchema, fetching, columns: baseColumns, data, pagination, create, update, _delete } = useAssetApi(queryParams);

  const handleNewButtonClick = useCallback(() => {
    setSelectedRow(null);
    setDrawerOpen(true);
  }, []);

  const handleEdit = useCallback((row) => {
    setSelectedRow({ ...row });
    setDrawerOpen(true);
  }, []);

  const handleSave = useCallback(async (formData) => {
    const success = selectedRow
      ? await update(selectedRow.id, formData)
      : await create(formData);
    if (success) setDrawerOpen(false);
    return success;
  }, [selectedRow, create, update]);

  const handleDelete = useCallback((row) => {
    setDeleteConfirm({ isOpen: true, ids: [row.id], name: row.name });
  }, []);

  const handleRowsDelete = useCallback((ids) => {
    setDeleteConfirm({ isOpen: true, ids, name: `${ids.length} ${t('asset.title').toLowerCase()}` });
  }, [t]);

  const handleDeleteConfirm = useCallback(async () => {
    setDeleting(true);
    await _delete(deleteConfirm.ids);
    setDeleteConfirm({ isOpen: false, ids: [], name: '' });
    setDeleting(false);
  }, [_delete, deleteConfirm.ids]);

  const handleDataStateChange = useCallback((action, tableState) => {
    setFilterList(tableState.filterList);
    const params = parseTableParams(tableState, baseColumns);
    setQueryParams(params);
  }, [baseColumns]);

  const handleFilterConfirm = useCallback((newFilterList) => {
    setFilterList(newFilterList);
  }, []);

  const handleRowClick = useCallback((row) => {
    history.push(`/assets/${row.id}`);
  }, [history]);

  const actionOptions = useMemo(() => {
    if (!canWrite('AM')) return [];
    return [
      { type: 'details', label: t('asset.edit'), onClick: (row) => history.push(`/assets/${row.id}`) },
      { type: 'edit', label: t('asset.edit'), onClick: handleEdit },
      { type: 'divider' },
      { type: 'delete', label: t('asset.delete'), onClick: handleDelete },
    ];
  }, [t, canWrite, handleEdit, handleDelete, history]);

  const columns = useMemo(() => {
    if (actionOptions.length === 0) return baseColumns;
    return [
      ...baseColumns,
      {
        name: 'actions',
        label: ' ',
        options: {
          filter: false, sort: false, download: false, print: false,
          customBodyRender: (_, tableMeta) => {
            const row = data[tableMeta.rowIndex];
            return <OptionsMenu actions={actionOptions} row={row} />;
          },
        },
      },
    ];
  }, [baseColumns, actionOptions, data]);

  return (
    <Container>
      <GenericFormDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        title={selectedRow ? t('asset.edit') : t('asset.new')}
        schema={objectSchema}
        initialValues={selectedRow}
        onSave={handleSave}
      />

      <ConfirmDialog
        isOpen={deleteConfirm.isOpen}
        title={t('asset.deleteTitle')}
        message={t('asset.deleteMessage', { name: deleteConfirm.name })}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteConfirm({ isOpen: false, ids: [], name: '' })}
        loading={deleting}
      />

      {useMemo(() => (
        <DataTable
          title={t('asset.title')}
          fetching={fetching}
          columns={columns}
          data={data}
          totalRowCount={pagination.totalRowCount}
          pageIndex={pagination.pageIndex}
          pageSize={pagination.pageSize}
          canCreateNewRecord={canWrite('AM')}
          toolbarActions={canWrite('AM') ? (
            <Button variant="outline" onClick={() => history.push('/asset-import')}>
              <Upload size={16} /> {t('assets.importButton')}
            </Button>
          ) : undefined}
          onNewButtonClick={handleNewButtonClick}
          onRowsDelete={canWrite('AM') ? handleRowsDelete : undefined}
          filterList={filterList}
          onFilterConfirm={handleFilterConfirm}
          onTableChanged={handleDataStateChange}
          onRowClick={handleRowClick}
        />
      ), [
        t, fetching, columns, data, pagination, canWrite, history,
        handleNewButtonClick, handleRowsDelete, filterList,
        handleFilterConfirm, handleDataStateChange, handleRowClick,
      ])}
    </Container>
  );
}
